const MAX_FACTS = 100;
const MAX_OBJECT_FIELDS = 16;
const MAX_TEXT_LENGTH = 300;

const LABEL_OVERRIDES = {
  monthly_cost: "Monthly cost",
  monthly_service_days: "Service days",
  source_population_count: "Source records",
  matched_population_count: "Matching records",
  source_row_count: "Source rows",
  rows_returned: "Result rows",
  groups_total: "Groups checked",
  groups_returned: "Groups returned",
};

const AUDIT_FACT_LABELS = new Set([
  "source population count",
  "matched population count",
  "source row count",
  "rows returned",
  "groups total",
  "groups returned",
  "pages fetched",
]);

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const safeText = (value, limit = MAX_TEXT_LENGTH) =>
  typeof value === "string"
    ? value.replace(/\0/g, " ").trim().slice(0, limit)
    : "";

const safeScalar = (value) => {
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") return safeText(value);
  return null;
};

const safeObject = (value) => {
  if (!isPlainObject(value)) return {};
  return Object.entries(value)
    .slice(0, MAX_OBJECT_FIELDS)
    .reduce((result, [key, item]) => {
      const safeKey = safeText(key, 80);
      const safeValue = safeScalar(item);
      if (safeKey && !["__proto__", "constructor", "prototype"].includes(safeKey)) {
        result[safeKey] = safeValue;
      }
      return result;
    }, {});
};

const safeValue = (value) => {
  if (isPlainObject(value)) return safeObject(value);
  if (Array.isArray(value)) {
    return value.slice(0, MAX_OBJECT_FIELDS).map(safeScalar);
  }
  return safeScalar(value);
};

const normalizedLabelKey = (value) =>
  safeText(value, 160).toLowerCase().replace(/[_\s]+/g, " ").trim();

export const humanizeEvidenceLabel = (value) => {
  const raw = safeText(value, 160);
  if (!raw) return "Value";
  const override = LABEL_OVERRIDES[raw.toLowerCase().replace(/[.\s]+/g, "_")];
  if (override) return override;
  return raw
    .replace(/[._]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bEui\b/g, "EUI")
    .replace(/\bKwh\b/g, "kWh")
    .replace(/\bMmbtu\b/g, "MMBtu")
    .replace(/\bUsd\b/g, "USD");
};

export const isAuditFact = (fact) => {
  const label = normalizedLabelKey(fact?.label);
  return (
    AUDIT_FACT_LABELS.has(label) ||
    /^(source|matched) population count$/.test(label) ||
    /^(rows|groups) (returned|total)$/.test(label)
  );
};

export const normalizeEvidenceFacts = (rawFacts, citationIds) => {
  if (!Array.isArray(rawFacts)) return [];
  const allowedCitationIds = citationIds instanceof Set ? citationIds : new Set();
  return rawFacts.slice(0, MAX_FACTS).reduce((result, item) => {
    const id = safeText(item?.id, 32);
    const label = safeText(item?.label, 160);
    const linkedCitationIds = Array.isArray(item?.citation_ids)
      ? item.citation_ids.filter((citationId) => allowedCitationIds.has(citationId)).slice(0, 8)
      : [];
    if (!/^fact-[1-9][0-9]*$/.test(id) || !label || !linkedCitationIds.length) {
      return result;
    }
    result.push({
      id,
      label,
      value: safeValue(item?.value),
      unit: safeText(item?.unit, 40),
      dimensions: safeObject(item?.dimensions),
      citationIds: linkedCitationIds,
    });
    return result;
  }, []);
};

export const normalizeSourceRecords = (records) => {
  if (!isPlainObject(records)) return null;
  const used = Number(records.used);
  const total = Number(records.total);
  const pagesFetched = Number(records.pages_fetched);
  if (![used, total, pagesFetched].every(Number.isFinite)) return null;
  return {
    used: Math.max(0, Math.trunc(used)),
    total: Math.max(0, Math.trunc(total)),
    pagesFetched: Math.max(0, Math.trunc(pagesFetched)),
    complete: records.complete === true,
  };
};

const citationIdsForFact = (fact) =>
  Array.isArray(fact?.citationIds)
    ? fact.citationIds
    : Array.isArray(fact?.citation_ids)
    ? fact.citation_ids
    : [];

const datasetTitle = (facts) => {
  const labels = [...new Set(facts.map((fact) => normalizedLabelKey(fact.label)))];
  if (labels.every((label) => /^observation \d+$/.test(label))) return "Records";
  return humanizeEvidenceLabel(facts[0]?.label || "Records");
};

export const buildEvidenceDataGroups = (facts, citations = []) => {
  const citationById = new Map(citations.map((citation) => [citation.id, citation]));
  const groupsById = new Map();

  (Array.isArray(facts) ? facts : []).filter((fact) => !isAuditFact(fact)).forEach((fact) => {
    const citationId = citationIdsForFact(fact)[0] || "verified-data";
    const citation = citationById.get(citationId);
    if (!groupsById.has(citationId)) {
      groupsById.set(citationId, {
        id: citationId,
        label: citation?.label || "Verified data",
        datasetsByShape: new Map(),
        metrics: [],
      });
    }
    const group = groupsById.get(citationId);
    const value = isPlainObject(fact.value) ? fact.value : null;
    if (!value || !Object.keys(value).length) {
      group.metrics.push({
        id: fact.id,
        label: humanizeEvidenceLabel(fact.label),
        value: fact.value,
        unit: fact.unit || "",
      });
      return;
    }

    const dimensions = isPlainObject(fact.dimensions) ? fact.dimensions : {};
    const dimensionKeys = Object.keys(dimensions);
    const valueKeys = Object.keys(value);
    const shape = `${[...dimensionKeys].sort().join("|")}::${[...valueKeys]
      .sort()
      .join("|")}`;
    if (!group.datasetsByShape.has(shape)) {
      group.datasetsByShape.set(shape, {
        id: `${citationId}-${group.datasetsByShape.size + 1}`,
        facts: [],
        dimensionKeys,
        valueKeys,
        rows: [],
      });
    }
    const dataset = group.datasetsByShape.get(shape);
    dataset.facts.push(fact);
    dataset.rows.push({
      id: fact.id,
      cells: { ...dimensions, ...value },
      units: valueKeys.length === 1 && fact.unit ? { [valueKeys[0]]: fact.unit } : {},
    });
  });

  return [...groupsById.values()].map((group) => ({
    id: group.id,
    label: group.label,
    metrics: group.metrics,
    datasets: [...group.datasetsByShape.values()].map((dataset) => ({
      id: dataset.id,
      title: datasetTitle(dataset.facts),
      columns: [...dataset.dimensionKeys, ...dataset.valueKeys].map((key) => ({
        key,
        label: humanizeEvidenceLabel(key),
        dimension: dataset.dimensionKeys.includes(key),
      })),
      rows: dataset.rows,
    })),
  }));
};

const formatDateText = (value) => {
  const monthMatch = /^(\d{4})-(\d{2})$/.exec(value);
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!monthMatch && !dateMatch) return value;
  const [, year, month, day = "01"] = dateMatch || monthMatch;
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);
  const date = new Date(Date.UTC(numericYear, numericMonth - 1, numericDay));
  if (
    Number.isNaN(date.getTime()) ||
    numericMonth < 1 ||
    numericMonth > 12 ||
    date.getUTCFullYear() !== numericYear ||
    date.getUTCMonth() !== numericMonth - 1 ||
    (dateMatch && date.getUTCDate() !== numericDay)
  ) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    ...(dateMatch ? { day: "numeric" } : {}),
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

export const formatEvidenceValue = (value, unit = "") => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([key, item]) => `${humanizeEvidenceLabel(key)}: ${formatEvidenceValue(item)}`)
      .join("; ");
  }
  if (Array.isArray(value)) return value.map((item) => formatEvidenceValue(item)).join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return formatDateText(value);
  if (typeof value !== "number" || !Number.isFinite(value)) return "Not available";

  const normalizedUnit = safeText(unit, 40).toLowerCase();
  if (["usd", "$"].includes(normalizedUnit)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  const number = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 4,
  }).format(value);
  if (normalizedUnit === "%") return `${number}%`;
  return unit ? `${number} ${unit}` : number;
};

export const formatSourceRecordSummary = (records) => {
  if (!records) return "";
  if (records.total === 0) {
    if (records.used > 0) {
      return `${records.used.toLocaleString("en-US")} verified result ${
        records.used === 1 ? "row" : "rows"
      } · Source record count unavailable`;
    }
    return records.complete
      ? "Complete check · No source records found"
      : "No source records found in the retrieved data";
  }
  const prefix = records.complete ? "Complete source" : "Partial source";
  const resultRows = records.used !== records.total ? ` · ${records.used} result rows used` : "";
  return `${prefix} · ${records.total.toLocaleString("en-US")} records checked${resultRows}`;
};

export const formatCheckStatus = (status) => {
  if (status === "passed") return "Verified";
  if (status === "failed") return "Needs attention";
  return "Not applicable";
};

export const formatCoverageStatus = (status) => {
  if (status === "supported") return "Fully answered";
  if (status === "partial") return "Partially answered";
  if (status === "unsupported") return "Not supported by the available data";
  if (status === "ambiguous") return "Needs clarification";
  return "In progress";
};
